import { useState } from 'react';

export default function Portfolio() {
  const [hoveredProject, setHoveredProject] = useState<number | null>(null);

  const templates = [
    {
      title: 'The Professional',
      description: 'Clean and authoritative design for established court reporters',
      pages: ['Home', 'About', 'Services', 'Testimonials', 'Contact'],
      features: ['Dark navy theme', 'Professional imagery', 'Trust badges', 'Client testimonials'],
      image: 'https://public.readdy.ai/ai/img_res/c82b94d74e7dc6fc0c0fb4e80a0662a9.jpg',
      color: '#1A2332',
      link: '/templates/professional'
    },
    {
      title: 'The Modern Reporter',
      description: 'Contemporary design with bold visuals for tech-savvy professionals',
      pages: ['Home', 'Services', 'Technology', 'Portfolio', 'Contact'],
      features: ['Minimalist layout', 'Large typography', 'Video backgrounds', 'Online booking'],
      image: 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20court%20reporting%20website%20with%20large%20bold%20typography%20clean%20white%20space%20contemporary%20design%20featuring%20technology%20section%20service%20cards%20and%20streamlined%20contact%20form%20with%20simple%20light%20background&width=600&height=600&seq=template-modern-02&orientation=squarish',
      color: '#2C3E50',
      link: '/templates/modern'
    },
    {
      title: 'The Legal Expert',
      description: 'Traditional and trustworthy design emphasizing experience',
      pages: ['Home', 'About Us', 'Court Reporting Services', 'Case Studies', 'Get Quote'],
      features: ['Classic serif fonts', 'Gold accents', 'Credentials display', 'Experience timeline'],
      image: 'https://readdy.ai/api/search-image?query=Traditional%20legal%20court%20reporter%20website%20with%20classic%20serif%20typography%20gold%20accent%20colors%20professional%20credentials%20section%20experience%20timeline%20and%20formal%20layout%20design%20with%20simple%20light%20background&width=800&height=600&seq=template-legal-03&orientation=landscape',
      color: '#8B7355',
      link: '/templates/legal'
    },
    {
      title: 'The Freelancer',
      description: 'Personal brand-focused design for independent court reporters',
      pages: ['Home', 'My Story', 'What I Offer', 'Client Reviews', 'Hire Me'],
      features: ['Personal photo hero', 'Story-driven content', 'Social proof', 'Easy scheduling'],
      image: 'https://readdy.ai/api/search-image?query=Personal%20freelance%20court%20reporter%20website%20with%20friendly%20approachable%20design%20featuring%20professional%20headshot%20hero%20section%20personal%20story%20about%20page%20client%20reviews%20and%20simple%20booking%20interface%20with%20simple%20light%20background&width=600&height=600&seq=template-freelancer-04&orientation=squarish',
      color: '#E74C3C',
      link: '/templates/freelancer'
    },
    {
      title: 'The Agency Pro',
      description: 'Multi-reporter firm design with team showcase',
      pages: ['Home', 'Our Team', 'Services & Rates', 'Coverage Areas', 'Request Reporter'],
      features: ['Team member profiles', 'Service area map', 'Rate calculator', 'Multi-location support'],
      image: 'https://readdy.ai/api/search-image?query=Court%20reporting%20agency%20website%20with%20team%20member%20grid%20professional%20staff%20photos%20service%20area%20coverage%20map%20rate%20calculator%20and%20multi-location%20contact%20information%20with%20simple%20light%20background&width=800&height=600&seq=template-agency-05&orientation=landscape',
      color: '#16A085',
      link: '/templates/agency'
    },
    {
      title: 'The Specialist',
      description: 'Niche-focused design for specialized court reporting services',
      pages: ['Home', 'Specializations', 'Why Choose Us', 'Resources', 'Schedule Consultation'],
      features: ['Specialty highlights', 'Industry certifications', 'Resource library', 'Expert positioning'],
      image: 'https://readdy.ai/api/search-image?query=Specialized%20court%20reporter%20website%20highlighting%20medical%20legal%20depositions%20with%20certification%20badges%20specialty%20service%20sections%20resource%20library%20and%20expert%20consultation%20booking%20with%20simple%20light%20background&width=600&height=600&seq=template-specialist-06&orientation=squarish',
      color: '#9B59B6',
      link: '/templates/specialist'
    },
    {
      title: 'The Executive',
      description: 'Luxury design for high-end court reporting services',
      pages: ['Home', 'Services', 'Expertise', 'Our Distinction', 'Contact'],
      features: ['Gold & charcoal theme', 'Premium positioning', 'Concierge services', 'White-glove experience'],
      image: 'https://readdy.ai/api/search-image?query=Luxury%20executive%20court%20reporter%20website%20with%20sophisticated%20gold%20and%20charcoal%20color%20scheme%20elegant%20typography%20premium%20service%20sections%20concierge%20features%20and%20high-end%20professional%20design%20with%20simple%20refined%20background&width=800&height=600&seq=template-executive-07&orientation=landscape',
      color: '#D4AF37',
      link: '/templates/executive'
    },
    {
      title: 'The Boutique',
      description: 'Warm, personalized design for small firms and solo practitioners',
      pages: ['Home', 'About', 'Services', 'Our Approach', 'Contact'],
      features: ['Warm coral tones', 'Personal touch', 'Small firm focus', 'Approachable design'],
      image: 'https://readdy.ai/api/search-image?query=Boutique%20court%20reporter%20website%20with%20warm%20coral%20and%20cream%20colors%20friendly%20approachable%20design%20personalized%20service%20sections%20intimate%20professional%20atmosphere%20and%20welcoming%20layout%20with%20simple%20clean%20background&width=600&height=600&seq=template-boutique-08&orientation=squarish',
      color: '#E8927C',
      link: '/templates/boutique'
    }
  ];

  const handleTemplateClick = (link: string) => {
    if (link.startsWith('#')) {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.REACT_APP_NAVIGATE(link);
    }
  };

  return (
    <section className="py-24 bg-[#1A2332]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="mb-16">
          <h2 className="font-['Playfair_Display'] text-5xl font-bold text-white mb-4">
            5-Page Website Templates
          </h2>
          <div className="w-20 h-1 bg-[#FF6B6B]"></div>
          <p className="text-gray-300 text-lg mt-6 max-w-2xl">
            Choose from our professionally designed 5-page website templates, each crafted specifically for court reporting professionals
          </p>
        </div>

        {/* Templates Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {templates.map((template, index) => (
            <div
              key={index}
              onMouseEnter={() => setHoveredProject(index)}
              onMouseLeave={() => setHoveredProject(null)}
              onClick={() => handleTemplateClick(template.link)}
              className={`relative rounded-xl overflow-hidden cursor-pointer transition-all duration-300 bg-white ${
                hoveredProject === index ? 'transform scale-105 shadow-2xl' : 'shadow-lg'
              } ${index === 0 ? 'lg:col-span-2' : ''}`}
            >
              {/* Template Preview Image */}
              <div className="relative h-64 w-full overflow-hidden">
                <img
                  src={template.image}
                  alt={template.title}
                  className={`w-full h-full object-cover object-top transition-transform duration-500 ${
                    hoveredProject === index ? 'scale-110' : 'scale-100'
                  }`}
                />
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                  <span className="text-xs font-semibold text-[#1A2332]">5 Pages</span>
                </div>
                <div className="absolute top-4 left-4 bg-[#FF6B6B] text-white px-3 py-1 rounded-full">
                  <span className="text-xs font-semibold">Preview Available</span>
                </div>
              </div>

              {/* Template Info */}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: template.color }}
                  ></div>
                  <h3 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A2332]">
                    {template.title}
                  </h3>
                </div>
                
                <p className="text-gray-600 text-sm mb-4">
                  {template.description}
                </p>

                {/* Pages Included */}
                <div className="mb-4">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Pages Included:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {template.pages.map((page, idx) => (
                      <span 
                        key={idx}
                        className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                      >
                        {page}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Key Features */}
                <div className="mb-4">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Key Features:
                  </div>
                  <ul className="space-y-1">
                    {template.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-checkbox-circle-fill text-[#FF6B6B] text-xs"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA Button */}
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTemplateClick(template.link);
                  }}
                  className="w-full bg-[#FF6B6B] text-white px-4 py-3 rounded-lg text-sm font-semibold hover:bg-[#ff5252] transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  Preview Template
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>

              {/* Hover Overlay */}
              {hoveredProject === index && (
                <div className="absolute inset-0 bg-gradient-to-t from-[#FF6B6B]/95 via-[#FF6B6B]/90 to-transparent flex flex-col items-center justify-center transition-opacity duration-300 p-6">
                  <div className="text-center text-white mb-6">
                    <h4 className="font-['Playfair_Display'] text-3xl font-bold mb-2">
                      {template.title}
                    </h4>
                    <p className="text-white/90 text-sm mb-4">
                      Complete 5-page website ready to launch
                    </p>
                    <div className="flex flex-wrap justify-center gap-2 mb-4">
                      {template.pages.map((page, idx) => (
                        <span 
                          key={idx}
                          className="text-xs bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full"
                        >
                          {page}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTemplateClick(template.link);
                    }}
                    className="bg-white text-[#FF6B6B] px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    View Full Template
                    <i className="ri-arrow-right-line ml-2"></i>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* All Templates Include */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 mb-12">
          <h3 className="font-['Playfair_Display'] text-2xl font-bold text-white mb-6 text-center">
            Every Template Includes:
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-[#FF6B6B] rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-phone-line text-xl text-white"></i>
              </div>
              <div className="text-white font-semibold mb-1">Toll-Free Number</div>
              <div className="text-gray-400 text-sm">Professional business line</div>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-[#FF6B6B] rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-smartphone-line text-xl text-white"></i>
              </div>
              <div className="text-white font-semibold mb-1">Mobile Responsive</div>
              <div className="text-gray-400 text-sm">Perfect on all devices</div>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-[#FF6B6B] rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-search-line text-xl text-white"></i>
              </div>
              <div className="text-white font-semibold mb-1">SEO Optimized</div>
              <div className="text-gray-400 text-sm">Rank higher on Google</div>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-[#FF6B6B] rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-customer-service-line text-xl text-white"></i>
              </div>
              <div className="text-white font-semibold mb-1">24/7 Support</div>
              <div className="text-gray-400 text-sm">Always here to help</div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-8 pt-16 border-t border-white/10">
          <div className="text-center">
            <div className="font-['Playfair_Display'] text-5xl font-bold text-[#FF6B6B] mb-2">8</div>
            <div className="text-gray-300">Professional Templates</div>
          </div>
          <div className="text-center">
            <div className="font-['Playfair_Display'] text-5xl font-bold text-[#FF6B6B] mb-2">5</div>
            <div className="text-gray-300">Pages Per Website</div>
          </div>
          <div className="text-center">
            <div className="font-['Playfair_Display'] text-5xl font-bold text-[#FF6B6B] mb-2">100%</div>
            <div className="text-gray-300">Customizable</div>
          </div>
          <div className="text-center">
            <div className="font-['Playfair_Display'] text-5xl font-bold text-[#FF6B6B] mb-2">$99</div>
            <div className="text-gray-300">Per Month</div>
          </div>
        </div>
      </div>
    </section>
  );
}
